import React from 'react'
import {
  Drawer,
  makeStyles,
  Typography,
  IconButton,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Form } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import useQueryParams from '../../hooks/useQueryParams'
import useGetLocalStorage from '../../hooks/useGetLocalStorage'
import localStorageIds from '../../utils/constants/localStorageIds'
import createSeasonTeamPlayer from '../../Apis/player/createSeasonTeamPlayer'
import updatePlayer from '../../Apis/player/updatePlayer'
import QueryKeys from '../../utils/constants/queryKeys'
import AddButton from '../../components/ui/AddButton/AddButton'
import MyAddLogoInput from '../../react-admin/components/inputs/MyAddLogoInput'
import MyTextInput from '../../react-admin/components/inputs/MyTextInput'
import MyNumberInput from '../../react-admin/components/inputs/MyNumberInput'
import MyDateField from '../../react-admin/components/inputs/MyDateField'
import MyAutocompleteArrayInput from '../../react-admin/components/inputs/MyAutocompleteArrayInput'
import FormGrid from '../../react-admin/components/layout/FormGrid/FormGrid'
import getPlayerPositions from '../../Apis/player/getPlayerPositions'
import getSeasonTeams from '../../Apis/season/getSeasonTeams'
import stringWithComma from '../../utils/helpers/stringWithComma'
import CustomIterator from '../../react-admin/components/CustomIterator/CustomIterator'

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: '31%',
    minWidth: 540,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    padding: theme.spacing(3),
    overflowY: 'auto',
  },
  footer: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
}))

const LeaguePlayerCreateDrawer = ({ open, onClose, player }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const queryParams = useQueryParams()
  const queryClient = useQueryClient()
  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_SEASON]: selectedSeason } = localStorageValue || {}
  const { id: seasonId } = selectedSeason || {}

  const isEditMode = Boolean(player)
  const initialLogoUrl = player?.logoUrl || ''

  const getLogoDataUri = (currentLogo) => {
    if (!isEditMode) return currentLogo || null
    if (currentLogo === initialLogoUrl) return null
    return currentLogo || ''
  }

  const { data: positionsData } = useQuery(
    [QueryKeys.GET_POSITIONS],
    getPlayerPositions
  )

  const { data: seasonTeams } = useQuery(
    [QueryKeys.GET_SEASON_TEAMS, queryParams?.org, seasonId],
    () => getSeasonTeams(queryParams?.org, seasonId),
    { enabled: Boolean(queryParams?.org && seasonId) }
  )

  const createMutation = useMutation(
    (playerData) => createSeasonTeamPlayer(queryParams?.org, playerData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_ORG_PLAYERS])
        onClose()
      },
    }
  )

  const updateMutation = useMutation(
    ({ playerId, playerData }) => updatePlayer(playerId, playerData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_ORG_PLAYERS])
        onClose()
      },
    }
  )

  const handleSubmit = (values) => {
    const positionIds = (values.positions || [])
      .map((positionName) => {
        const position = positionsData?.data?.find((p) => p.name === positionName)
        return position ? position.id : null
      })
      .filter((id) => id !== null)

    const basePayload = {
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth || null,
      height: values.height ? parseFloat(values.height) : null,
      logoDataUri: getLogoDataUri(values.logo),
      positions: positionIds,
    }

    if (isEditMode) {
      updateMutation.mutate({
        playerId: player.id,
        playerData: {
          id: player.id,
          personalData: {
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: values.dateOfBirth || null,
            height: values.height ? parseFloat(values.height) : null,
            email: player.personalData?.email || null,
            phoneNumber: player.personalData?.phoneNumber || null,
            positions: positionIds,
            age: player.personalData?.age || null,
          },
          clothingData: {
            shoeSize: player.clothingData?.shoeSize || null,
            jerseySize: player.clothingData?.jerseySize || null,
            shortsSize: player.clothingData?.shortsSize || null,
          },
          logoDataUri: getLogoDataUri(values.logo),
        },
      })
    } else {
      const teams = values.teams || []
      teams.forEach((team) => {
        if (team.team) {
          createMutation.mutate({
            ...basePayload,
            seasonTeamId: team.team,
            jerseyNumber: team.jerseyNumber ? parseInt(team.jerseyNumber) : null,
          })
        }
      })
      onClose()
    }
  }

  const validateForm = (values) => {
    const errors = {}
    if (!values.firstName || !values.firstName.trim()) {
      errors.firstName = translate('ra.validation.required')
    }
    if (!values.lastName || !values.lastName.trim()) {
      errors.lastName = translate('ra.validation.required')
    }
    return errors
  }

  const getInitialValues = () => {
    if (isEditMode && player) {
      const positionNames = player.personalData?.positions?.map((p) => p.name) || []
      const teams = player.teams?.map((t) => ({
        team: t.team,
        jerseyNumber: t.jerseyNumber || '',
      })) || [{ team: '', jerseyNumber: '' }]
      return {
        logo: player.logoUrl || null,
        teams,
        firstName: player.personalData?.firstName || '',
        lastName: player.personalData?.lastName || '',
        dateOfBirth: player.personalData?.dateOfBirth || null,
        height: player.personalData?.height || '',
        positions: positionNames,
      }
    }
    return {
      logo: null,
      teams: [{ team: '', jerseyNumber: '' }],
      firstName: '',
      lastName: '',
      dateOfBirth: null,
      height: '',
      positions: [],
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawer }}
    >
      <div className={classes.header}>
        <Typography variant="h6">
          {isEditMode ? translate('ra.buttons.editPlayer') || 'Edit Player' : translate('ra.buttons.addPlayer')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <Form
        key={player?.id || 'new'}
        onSubmit={handleSubmit}
        validate={validateForm}
        initialValues={getInitialValues()}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <div className={classes.content}>
              <FormGrid container spacing={2}>
                <MyAddLogoInput source="logo" />
              </FormGrid>
              <FormGrid container spacing={2}>
                <MyTextInput
                  source="firstName"
                  label={translate('ra.formFields.firstName')}
                  sm={12}
                  required
                />
                <MyTextInput
                  source="lastName"
                  label={translate('ra.formFields.lastName')}
                  sm={12}
                  required
                />
              </FormGrid>
              <FormGrid container spacing={2}>
                <MyDateField source="dateOfBirth" label='Date Of Birth' />
                <MyNumberInput
                  source="height"
                  label='Height'
                  parse={(height) => stringWithComma(height)}
                  step={0.1}
                  min={0}
                />
              </FormGrid>
              <FormGrid container spacing={2}>
                <MyAutocompleteArrayInput
                  source="positions"
                  label={translate('ra.formFields.positions') || 'Positions'}
                  choices={positionsData?.data || []}
                  optionText="name"
                  optionValue="name"
                  resettable
                />
              </FormGrid>
              <CustomIterator
                fieldArrayName="teams"
                teamChoices={seasonTeams}
                optionValue='id'
                disabled={false}
                hideAddButton
              />
            </div>
            <div className={classes.footer}>
              <AddButton
                label={translate('ra.action.cancel')}
                onClick={onClose}
                disabled={submitting}
              />
              <AddButton
                label={translate('ra.action.save')}
                onClick={handleSubmit}
                disabled={submitting}
                filled
              />
            </div>
          </form>
        )}
      />
    </Drawer>
  )
}

export default LeaguePlayerCreateDrawer
